import store from '@/store'
import { modelNameSingularize } from '@/utils'

/**
 * Parse and structure privileges from API response
 *
 * @param {string[]} apiResponse - Raw privileges array from API
 * @returns {Object} Structured privileges object
 */
function buildPrivileges(apiResponse) {
  if (!Array.isArray(apiResponse) || apiResponse.length === 0) {
    console.warn(
      '[auth] buildPrivileges: no privileges found — user will have no privileges',
    )
    return {}
  }

  const privileges = {}

  // Special case privileges that don't follow the standard pattern
  const specialPrivileges = {
    stocks_send_order_to_store: {
      app: 'stocks',
      model: 'special',
      action: 'send_order_to_store',
    },
    stocks_create_generic_order: {
      app: 'stocks',
      model: 'special',
      action: 'create_generic_order',
    },
  }

  for (const privilege of apiResponse) {
    let app = ''
    let model = ''
    let action = ''

    // Check if this is a special privilege
    if (specialPrivileges[privilege]) {
      const special = specialPrivileges[privilege]
      app = special.app
      model = special.model
      action = special.action
    } else {
      // Standard pattern: app_action_model
      const matchPvs = /(.+?)_(view|add|change|delete)_(.+)/.exec(privilege)
      if (matchPvs !== null) {
        app = matchPvs[1]
        model = matchPvs[3]
        action = matchPvs[2]
      }
    }

    // Add privilege to structured object if all parts were determined
    if (app && model && action) {
      // Initialize nested objects if they don't exist
      privileges[app] = privileges[app] || {}
      privileges[app][model] = privileges[app][model] || []

      // Add the action to the model's actions array
      privileges[app][model].push(action)
    }
  }

  return privileges
}

/**
 * Check if the current user has a specific privilege
 *
 * @param {string} app - The application name
 * @param {string} model - The model name
 * @param {string} action - The action name
 * @returns {boolean} Whether the user has the privilege
 */
function hasPrivilege(app, model, action) {
  const privileges = store.state.auth.privileges

  // Normalize model name
  let modelName = model
  if (modelName !== 'reports' && modelName !== 'special') {
    modelName = modelNameSingularize(model)
  }

  // Check if privilege exists
  return Boolean(
    privileges &&
      privileges[app] &&
      privileges[app][modelName] &&
      privileges[app][modelName].includes(action),
  )
}

/**
 * Check if user has permission to view a specific model
 *
 * @param {string} app - The application name
 * @param {string} model - The model name
 * @returns {boolean} Whether user can view the model
 */
function canView(app, model) {
  return hasPrivilege(app, model, 'view')
}

/**
 * Check if user has permission to add a specific model
 *
 * @param {string} app - The application name
 * @param {string} model - The model name
 * @returns {boolean} Whether user can add the model
 */
function canAdd(app, model) {
  return hasPrivilege(app, model, 'add')
}

/**
 * Check if user has permission to change a specific model
 *
 * @param {string} app - The application name
 * @param {string} model - The model name
 * @returns {boolean} Whether user can change the model
 */
function canChange(app, model) {
  return hasPrivilege(app, model, 'change')
}

/**
 * Check if user has permission to delete a specific model
 *
 * @param {string} app - The application name
 * @param {string} model - The model name
 * @returns {boolean} Whether user can delete the model
 */
function canDelete(app, model) {
  return hasPrivilege(app, model, 'delete')
}

// For backward compatibility
function createLegacyHasPrivilege() {
  return function (app, model, action) {
    return hasPrivilege(app, model, action)
  }
}

export {
  buildPrivileges,
  hasPrivilege,
  canView,
  canAdd,
  canChange,
  canDelete,
  createLegacyHasPrivilege,
}
